import { useNavigate, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { Settings as SettingsIcon, ChevronRight, KeyRound, Bell, BellOff, Download } from "lucide-react";
import { BrutalCard, SectionTitle } from "@/components/Brutal";
import { DatePicker } from "@/components/DatePicker";
import { CONTENT_MODULES } from "@/lib/labels";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/Brutal";
import { PhotoInput, initials } from "@/components/AiComponents";
import { enablePush, disablePush, isPushEnabled, pushSupported } from "@/lib/push";
import NotifPrefsDialog from "@/components/NotifPrefsDialog";
import { useConfirm } from "@/components/ConfirmProvider";
import api from "@/lib/api";
import { normalizePhone, isValidPhone } from "@/lib/phone";
import { toast } from "sonner";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = () => window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;

export default function Meer() {
  const navigate = useNavigate();
  const { user, refresh, moduleOn, orgs, activeOrg, switchOrg, refreshOrgs } = useAuth();
  const t = useT();
  const confirm = useConfirm();
  const [pinOpen, setPinOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingJoins, setPendingJoins] = useState(0);
  const appLink = window.location.origin;
  const [pin, setPin] = useState("");
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState({ name: "", phone: "", birthdate: "", avatar_url: null, emergency_contact: { name: "", phone: "", member_id: "" }, pets: [] });
  const [members, setMembers] = useState([]);
  const [pushOn, setPushOn] = useState(false);
  const [installable, setInstallable] = useState(!!window.__doeniesInstallPrompt);
  const [busy, setBusy] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [installMode, setInstallMode] = useState("app");
  const [iosPulse, setIosPulse] = useState(false);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [refCount, setRefCount] = useState(0);
  const [delAccountOpen, setDelAccountOpen] = useState(false);
  const [delAccountBusy, setDelAccountBusy] = useState(false);
  const [churnReason, setChurnReason] = useState("");
  const [churnText, setChurnText] = useState("");
  const bannerLang = (user?.language && ["en", "tr", "ar"].includes(user.language)) ? user.language : "nl";
  const banners = [1, 2, 3, 4, 5].map((n) => bannerLang === "nl" ? `/slackr-promo-${n}.png` : `/slackr-promo-${n}-${bannerLang}.png`);
  const [kioskCode, setKioskCode] = useState(null);
  const [kioskDialogOpen, setKioskDialogOpen] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [orgId, setOrgId] = useState(null);
  const effOrg = activeOrg || orgId;
  const activeOrgObj = orgs?.find((o) => o.org_id === activeOrg);
  const kioskPath = `/kiosk${effOrg ? `?org=${effOrg}` : ""}`;
  const kioskQr = `${appLink}/api/app-qr?base_url=${encodeURIComponent(appLink)}&path=${encodeURIComponent(kioskPath)}`;
  const joinPath = `/aanmelden${effOrg ? `?org=${effOrg}${user?.user_id ? `&ref=${user.user_id}` : ""}` : ""}`;
  const joinOrgLink = `${appLink}${joinPath}`;
  const joinQr = `${appLink}/api/app-qr?base_url=${encodeURIComponent(appLink)}&path=${encodeURIComponent(joinPath)}`;
  const showIosHint = isIOS() && !isStandalone();

  useEffect(() => {
    isPushEnabled().then(setPushOn);
    api.get("/users").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/referrals/count").then((r) => setRefCount(r.data.count)).catch(() => {});
    if (isAdmin(user)) api.get("/join-requests").then((r) => setPendingJoins(r.data.length)).catch(() => {});
    const onInstall = () => setInstallable(true);
    window.addEventListener("pwa-installable", onInstall);
    return () => window.removeEventListener("pwa-installable", onInstall);
  }, []);

  const togglePush = async () => {
    if (!pushOn) {
      const ok = await confirm({ title: "Meldingen aanzetten?", description: "Slackr stuurt je een seintje bij nieuwe taken, herinneringen, mededelingen en polls. Je browser vraagt daarna nog eenmalig om toestemming.", confirmLabel: "Aanzetten", tone: "info" });
      if (!ok) return;
    }
    setBusy(true);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success("Meldingen uit"); }
      else { await enablePush(); setPushOn(true); toast.success("Meldingen aan"); }
    } catch (e) { toast.error(e.message || "Mislukt"); }
    finally { setBusy(false); }
  };

  const installApp = () => {
    setInstallMode("app"); setInstallOpen(true);
    if (!orgId) api.get("/settings/public").then((r) => setOrgId(r.data.org_id)).catch(() => {});
    if (isAdmin(user) && !kioskCode) api.get("/kiosk-code").then((r) => setKioskCode(r.data.code)).catch(() => {});
  };

  const location = useLocation();
  useEffect(() => {
    if (location.state?.openInstall) {
      installApp();
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const openKioskCode = () => {
    setKioskDialogOpen(true);
    if (!orgId) api.get("/settings/public").then((r) => setOrgId(r.data.org_id)).catch(() => {});
    api.get("/kiosk-code").then((r) => setKioskCode(r.data.code)).catch(() => {});
  };

  const regenerateKiosk = async () => {
    const ok = await confirm({ title: "Nieuwe kioskcode maken?", description: "De huidige code werkt daarna niet meer. Bestaande kiosk-tablets blijven ingelogd, maar bij een nieuwe installatie is de nieuwe code nodig.", confirmLabel: "Vernieuwen", tone: "warning" });
    if (!ok) return;
    setRegenBusy(true);
    try { const r = await api.post("/kiosk/regenerate-code"); setKioskCode(r.data.code); toast.success("Nieuwe kioskcode aangemaakt"); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
    finally { setRegenBusy(false); }
  };

  const doInstall = async () => {
    const prompt = window.__doeniesInstallPrompt;
    if (!prompt) { toast.info("Gebruik het browsermenu: 'Toevoegen aan beginscherm'."); return; }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") { toast.success("Slackr wordt geïnstalleerd!"); setInstallOpen(false); }
    window.__doeniesInstallPrompt = null;
    setInstallable(false);
  };

  const showIosSteps = () => {
    setInstallMode("app");
    setIosPulse(true);
    setTimeout(() => setIosPulse(false), 6000);
  };

  const promoCaption = () => {
    const org = activeOrgObj?.name || (bannerLang === "en" ? "our household" : bannerLang === "tr" ? "evimizi" : bannerLang === "ar" ? "منزلنا" : "ons huishouden");
    const link = effOrg ? joinOrgLink : appLink;
    if (bannerLang === "en") return `We run ${org} with Slackr — tasks, groceries, recipes & plans together, without endless texting.\n\nJoin us (organization already linked): ${link}\n\n#Slackr #household #livingtogether`;
    if (bannerLang === "tr") return `${org} işlerini Slackr ile hallediyoruz — görevler, alışveriş, tarifler ve planlar birlikte, sonu gelmeyen mesajlaşma olmadan.\n\nSen de katıl (organizasyon zaten bağlı): ${link}\n\n#Slackr #evdüzeni #birlikteyaşam`;
    if (bannerLang === "ar") return `ننظّم ${org} مع Slackr — المهام والتسوّق والوصفات والمواعيد معًا، بدون رسائل لا تنتهي.\n\nانضم إلينا (المؤسسة مرتبطة بالفعل): ${link}\n\n#Slackr`;
    return `Wij regelen ${org} met Slackr — taken, boodschappen, recepten & afspraken sámen, zónder eindeloos te appen.\n\nSluit je aan (organisatie is al gekoppeld): ${link}\n\n#Slackr #huishouden #samenwonen`;
  };

  const shareBanner = async () => {
    const src = banners[bannerIdx];
    const caption = promoCaption();
    try { await navigator.clipboard?.writeText(caption); } catch (e) { /* ignore */ }
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const file = new File([blob], "slackr-promo.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: "Slackr" });
        toast.success(t("Bijschrift gekopieerd — plakken en delen maar!"));
        return;
      }
    } catch (e) { /* fall through to download */ }
    const a = document.createElement("a");
    a.href = src; a.download = "slackr-promo.png";
    document.body.appendChild(a); a.click(); a.remove();
    toast.info(t("Banner gedownload + bijschrift gekopieerd — deel 'm op je Instagram- of Facebook-verhaal en plak het bijschrift."));
  };

  const copyCaption = async () => {
    try { await navigator.clipboard?.writeText(promoCaption()); toast.success(t("Bijschrift gekopieerd")); }
    catch (e) { toast.error(t("Kopiëren mislukt")); }
  };

  const downloadBanner = () => {
    const a = document.createElement("a");
    a.href = banners[bannerIdx]; a.download = "slackr-promo.png";
    document.body.appendChild(a); a.click(); a.remove();
    toast.success(t("Banner opgeslagen"));
  };

  const leaveOrg = async () => {
    const org = activeOrgObj;
    if (!org || !activeOrg) return;
    const isLast = (orgs?.length || 0) <= 1;
    const ok = await confirm({
      title: `${org.name} verlaten?`,
      description: isLast
        ? "Dit is je enige organisatie. Als je de laatste bewoner bent, worden de organisatie én álle data (taken, boodschappen, recepten…) definitief verwijderd."
        : `Je verliest toegang tot ${org.name} en de bijbehorende data. Je andere organisaties blijven gewoon behouden.`,
      confirmLabel: "Verlaten", tone: "warning",
    });
    if (!ok) return;
    try {
      const r = await api.delete(`/organizations/${activeOrg}/leave`);
      toast.success(r.data?.deleted ? `${org.name} is verwijderd` : `Je hebt ${org.name} verlaten`);
      localStorage.removeItem("slackr_org");
      await refreshOrgs();
      window.location.href = "/vandaag";
    } catch (e) { toast.error(e.response?.data?.detail || "Verlaten mislukt"); }
  };

  const savePin = async () => {
    try { await api.post("/auth/set-pin", { pin }); toast.success("Pincode ingesteld"); setPinOpen(false); setPin(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  const openProfile = () => {
    setProfile({
      name: user.name || "", phone: user.phone || "", birthdate: user.birthdate || "", avatar_url: user.avatar_url || null,
      emergency_contact: user.emergency_contact || { name: "", phone: "", member_id: "" },
      pets: user.pets || [], language: user.language || "nl",
    });
    setProfileOpen(true);
  };
  const setEc = (patch) => setProfile((p) => ({ ...p, emergency_contact: { ...p.emergency_contact, ...patch } }));
  const addPet = () => setProfile((p) => ({ ...p, pets: [...p.pets, { id: crypto.randomUUID(), name: "", species: "", chip_id: "", description: "", photo_url: null }] }));
  const updatePet = (id, patch) => setProfile((p) => ({ ...p, pets: p.pets.map((x) => x.id === id ? { ...x, ...patch } : x) }));
  const removePet = (id) => setProfile((p) => ({ ...p, pets: p.pets.filter((x) => x.id !== id) }));
  const saveProfile = async () => {
    if (profile.phone && !isValidPhone(profile.phone)) { toast.error("Vul een geldig telefoonnummer in of laat het leeg"); return; }
    try {
      await api.put("/auth/profile", { ...profile, phone: profile.phone ? normalizePhone(profile.phone) : profile.phone });
      if (profile.language) localStorage.setItem("slackr_lang", profile.language);
      await refresh();
      toast.success("Profiel opgeslagen");
      setProfileOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Mislukt"); }
  };

  const deleteAccount = async () => {
    setDelAccountOpen(true);
  };

  const confirmDeleteAccount = async () => {
    setDelAccountBusy(true);
    try {
      await api.delete("/auth/me", { params: { reason: churnReason, reason_text: churnText } });
      localStorage.removeItem("doenies_token");
      localStorage.removeItem("slackr_org");
      toast.success("Je account is opgeheven");
      window.location.href = "/login";
    } catch (e) { toast.error(e.response?.data?.detail || "Opheffen mislukt"); setDelAccountBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="meer-page">
      <SectionTitle sub={t("Alle modules op één plek")}>{t("Meer")}</SectionTitle>

      <div className="grid grid-cols-2 gap-3">
        {Object.entries(CONTENT_MODULES).filter(([key]) => moduleOn(key)).map(([key, m]) => {
          const Icon = Icons[m.icon] || Icons.Square;
          const bg = { yellow: "bg-brand-yellow", pink: "bg-brand-pink", green: "bg-brand-green", blue: "bg-brand-blue text-white", orange: "bg-brand-orange text-white", white: "bg-white" }[m.color];
          return (
            <button key={key} data-testid={`module-${key}`} onClick={() => navigate(`/meer/${key}`)}
              className={`brutal brutal-press rounded-xl p-4 text-left ${bg}`}>
              <Icon className="w-7 h-7 mb-3" strokeWidth={2.5} />
              <p className="font-head font-bold leading-tight">{t(m.title)}</p>
              <p className="text-xs font-semibold opacity-70 mt-0.5">{t(m.sub)}</p>
            </button>
          );
        })}
        {moduleOn("hoppa") && (
        <button data-testid="hoppa-btn" onClick={() => navigate("/hoppa")}
          className="brutal brutal-press rounded-xl p-4 text-left sunset-gradient text-white">
          <Icons.Store className="w-7 h-7 mb-3" strokeWidth={2.5} />
          <p className="font-head font-bold leading-tight">{t("Hoppa")}</p>
          <p className="text-xs font-semibold opacity-80 mt-0.5">{t("Huur, koop, leen of gratis")}</p>
        </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {moduleOn("klusjes") && (
        <button data-testid="klusjes-btn" onClick={() => navigate("/klusjes")}
          className="brutal brutal-press rounded-xl p-4 text-left bg-brand-orange text-white">
          <Icons.Hammer className="w-7 h-7 mb-3" strokeWidth={2.5} />
          <p className="font-head font-bold leading-tight">{t("Klusjes")}</p>
          <p className="text-xs font-semibold opacity-70 mt-0.5">{t("Meld & los reparaties op")}</p>
        </button>
        )}
        {moduleOn("ruilmarkt") && (
        <button data-testid="ruilmarkt-btn" onClick={() => navigate("/ruilmarkt")}
          className="brutal brutal-press rounded-xl p-4 text-left bg-brand-green">
          <Icons.Repeat className="w-7 h-7 mb-3" strokeWidth={2.5} />
          <p className="font-head font-bold leading-tight">{t("Ruilmarkt")}</p>
          <p className="text-xs font-semibold opacity-70 mt-0.5">{t("Ruil of geef taken door")}</p>
        </button>
        )}
        {moduleOn("eten") && (
        <button data-testid="eten-btn" onClick={() => navigate("/eten")}
          className="brutal brutal-press rounded-xl p-4 text-left bg-brand-pink">
          <Icons.UtensilsCrossed className="w-7 h-7 mb-3" strokeWidth={2.5} />
          <p className="font-head font-bold leading-tight">{t("Eten")}</p>
          <p className="text-xs font-semibold opacity-70 mt-0.5">{t("Menu, recepten & wie kookt")}</p>
        </button>
        )}
        {moduleOn("polls") && (
        <button data-testid="poll-btn" onClick={() => navigate("/eten?tab=poll&new=1&algemeen=1")}
          className="brutal brutal-press rounded-xl p-4 text-left bg-brand-yellow">
          <Icons.BarChart3 className="w-7 h-7 mb-3" strokeWidth={2.5} />
          <p className="font-head font-bold leading-tight">{t("Stemming")}</p>
          <p className="text-xs font-semibold opacity-70 mt-0.5">{t("Start een poll over van alles")}</p>
        </button>
        )}
      </div>

      <div className="space-y-3">
        {orgs?.length > 0 && (
          <div className="brutal rounded-xl p-4 bg-white" data-testid="org-switcher">
            <div className="flex items-center gap-2 mb-2">
              <div className="brutal-sm rounded-lg bg-brand-violet text-white p-2"><Icons.Building2 className="w-5 h-5" strokeWidth={2.5} /></div>
              <div><p className="font-head font-bold leading-tight">{t("Actieve organisatie")}</p><p className="text-xs text-brand-ink/50">{t("Taken & data horen bij deze organisatie")}</p></div>
            </div>
            <div className="space-y-1.5">
              {orgs.map((o) => (
                <button key={o.org_id} data-testid={`org-option-${o.org_id}`} onClick={() => o.org_id !== activeOrg && switchOrg(o.org_id)}
                  className={`w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-left ${o.org_id === activeOrg ? "bg-brand-violet text-white" : "brutal-sm bg-white"}`}>
                  <span><span className="font-bold text-sm">{o.name}</span><span className={`block text-xs ${o.org_id === activeOrg ? "text-white/70" : "text-brand-ink/50"}`}>{o.type} · {o.role}</span></span>
                  {o.org_id === activeOrg && <Icons.Check className="w-5 h-5" strokeWidth={2.5} />}
                </button>
              ))}
            </div>
            <button data-testid="org-add-btn" onClick={() => navigate("/aanmelden")}
              className="w-full mt-2 text-sm font-bold text-brand-violet flex items-center justify-center gap-1.5 py-1.5"><Icons.Plus className="w-4 h-4" strokeWidth={2.5} />{t("Organisatie toevoegen of aansluiten")}</button>
            {activeOrg && (
              <button data-testid="org-leave-btn" onClick={leaveOrg}
                className="w-full text-sm font-bold text-brand-pink flex items-center justify-center gap-1.5 py-1.5"><Icons.LogOut className="w-4 h-4" strokeWidth={2.5} />{t("Deze organisatie verlaten")}</button>
            )}
          </div>
        )}

        <button data-testid="groepen-btn" onClick={() => navigate("/groepen")}
          className="w-full brutal brutal-press rounded-xl p-4 bg-brand-blue text-white flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-white/20 p-2"><Icons.Users className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Gebruikers & Groepen")}</p><p className="text-xs text-white/70">{t("Beheer groepen en leden")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="tijdlijn-btn" onClick={() => navigate("/tijdlijn")}
          className="w-full brutal brutal-press rounded-xl p-4 ocean-gradient text-white flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-white/20 p-2"><Icons.Activity className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Tijdslijn")}</p><p className="text-xs text-white/70">{t("Wie deed wat, en wanneer")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="punten-btn" onClick={() => navigate("/punten")}
          className="w-full brutal brutal-press rounded-xl p-4 bg-brand-violet text-white flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-white/20 p-2"><Icons.Trophy className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Punten & ranglijst")}</p><p className="text-xs text-white/70">{t("Verdien punten met taken & polls")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        {showIosHint && (
          <div data-testid="ios-push-hint" className="brutal-sm rounded-xl p-4 bg-brand-yellow flex items-start gap-3">
            <Icons.Smartphone className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2.5} />
            <p className="text-sm font-semibold">
              <b>iPhone/iPad?</b> Voeg Slackr eerst toe aan je beginscherm: tik op het deel-icoon in Safari en kies <b>"Zet op beginscherm"</b>. Daarna werken push-meldingen.
            </p>
          </div>
        )}

        <button data-testid="notif-prefs-btn" onClick={() => setNotifOpen(true)}
          className="w-full brutal brutal-press rounded-xl p-4 bg-white flex items-center gap-3">
          <div className={`brutal-sm rounded-lg p-2 ${pushOn ? "bg-brand-green" : "bg-brand-pink"}`}>{pushOn ? <Bell className="w-5 h-5" strokeWidth={2.5} /> : <Icons.SlidersHorizontal className="w-5 h-5" strokeWidth={2.5} />}</div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Meldingsvoorkeuren")}</p><p className="text-xs text-brand-ink/50">{t("Push aan/uit, triggers, stille uren & tijdzone")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="install-app-btn" onClick={installApp}
          className="w-full brutal brutal-press rounded-xl p-4 lime-gradient text-white flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-white/20 p-2"><Download className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("App installeren")}</p><p className="text-xs text-white/70">{t("Zet Slackr op je beginscherm")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="abonnement-btn" onClick={() => navigate("/abonnement")}
          className="w-full brutal brutal-press rounded-xl p-4 gold-gradient flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-white/40 p-2"><Icons.Crown className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Mijn abonnement")}</p><p className="text-xs opacity-70">{t("Proefperiode, licentie & upgraden")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="tour-btn" onClick={() => window.dispatchEvent(new Event("open-tour"))} className="w-full brutal brutal-press rounded-xl p-4 bg-white flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-brand-violet text-white p-2"><Icons.Rocket className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Rondleiding")}</p><p className="text-xs text-brand-ink/50">{t("Bekijk de tutorial opnieuw (NL/EN/TR/AR)")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="tickets-btn" onClick={() => navigate("/tickets")} className="w-full brutal brutal-press rounded-xl p-4 bg-white flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-brand-pink text-white p-2"><Icons.MessageSquareWarning className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Fouten & Suggesties")}</p><p className="text-xs text-brand-ink/50">{t("Meld een fout of deel een suggestie")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="profile-btn" onClick={openProfile} className="w-full brutal brutal-press rounded-xl p-4 bg-white flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-brand-violet p-2 text-white"><Icons.UserCircle className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Mijn profiel")}</p><p className="text-xs text-brand-ink/50">{t("Foto, telefoon, geboortedatum, noodnummer & huisdieren")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="invite-btn" onClick={() => setInviteOpen(true)} className="w-full brutal brutal-press rounded-xl p-4 bg-brand-pink flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-white/40 p-2"><Icons.UserPlus className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Uitnodigen & Aanbevelen")}</p><p className="text-xs text-brand-ink/60">{t("Iemand toevoegen of Slackr aanbevelen")}</p></div>
          {isAdmin(user) && pendingJoins > 0 && <span data-testid="invite-pending-badge" className="bg-brand-ink text-white text-xs font-black rounded-full min-w-[22px] h-[22px] px-1.5 flex items-center justify-center">{pendingJoins}</span>}
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <button data-testid="set-pin-btn" onClick={() => setPinOpen(true)} className="w-full brutal brutal-press rounded-xl p-4 bg-white flex items-center gap-3">
          <div className="brutal-sm rounded-lg bg-brand-green p-2"><KeyRound className="w-5 h-5" strokeWidth={2.5} /></div>
          <div className="flex-1 text-left"><p className="font-head font-bold">{t("Pincode instellen")}</p><p className="text-xs text-brand-ink/50">{t("Snel inloggen op de kiosk")}</p></div>
          <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
        </button>

        {isAdmin(user) && (
          <button data-testid="kiosk-code-btn" onClick={openKioskCode} className="w-full brutal brutal-press rounded-xl p-4 bg-brand-violet text-white flex items-center gap-3">
            <div className="brutal-sm rounded-lg bg-white/20 p-2"><Icons.MonitorSmartphone className="w-5 h-5" strokeWidth={2.5} /></div>
            <div className="flex-1 text-left"><p className="font-head font-bold">{t("Kioskcode")}</p><p className="text-xs text-white/60">{t("Toon of vernieuw de code voor de tablet")}</p></div>
            <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
          </button>
        )}

        {isAdmin(user) && (
          <button data-testid="klussen-pool-btn" onClick={() => navigate("/klussen-pool")} className="w-full brutal brutal-press rounded-xl p-4 bg-brand-orange text-white flex items-center gap-3">
            <div className="brutal-sm rounded-lg bg-white/20 p-2"><Icons.Hammer className="w-5 h-5" strokeWidth={2.5} /></div>
            <div className="flex-1 text-left"><p className="font-head font-bold">{t("Klussen Pool")}</p><p className="text-xs text-white/70">{t("Beheer inhaalklussen & punten")}</p></div>
            <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
          </button>
        )}

        {isAdmin(user) && (
          <button data-testid="settings-btn" onClick={() => navigate("/instellingen")} className="w-full brutal brutal-press rounded-xl p-4 navy-gradient text-white flex items-center gap-3">
            <div className="brutal-sm rounded-lg bg-brand-gold p-2"><SettingsIcon className="w-5 h-5" strokeWidth={2.5} /></div>
            <div className="flex-1 text-left"><p className="font-head font-bold">{t("Instellingen")}</p><p className="text-xs text-white/60">{t("Beheer gebruikers, rollen & parameters")}</p></div>
            {pendingJoins > 0 && <span data-testid="settings-pending-badge" className="bg-brand-pink text-white text-xs font-black rounded-full min-w-[22px] h-[22px] px-1.5 flex items-center justify-center">{pendingJoins}</span>}
            <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
          </button>
        )}
      </div>

      <Dialog open={kioskDialogOpen} onOpenChange={setKioskDialogOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-y-auto" data-testid="kiosk-code-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><Icons.MonitorSmartphone className="w-5 h-5" strokeWidth={2.5} />Kioskcode</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/70">Zet Slackr als infobord op een tablet (11"). Scan de QR met de tablet, zet 'm op het beginscherm en voer daarna deze verificatiecode één keer in.</p>
          {effOrg && (
            <div className="flex justify-center">
              <img src={kioskQr} alt="QR kiosk" width="160" height="160" className="brutal-sm rounded-xl bg-white p-1" data-testid="kiosk-dialog-qr" />
            </div>
          )}
          <div className="brutal-sm rounded-xl p-4 bg-brand-yellow text-center">
            <p className="text-xs font-bold uppercase tracking-wide flex items-center justify-center gap-1.5"><KeyRound className="w-4 h-4" strokeWidth={2.5} />Verificatiecode</p>
            <p className="font-head font-black text-3xl tracking-[0.3em] mt-1" data-testid="kiosk-dialog-code">{kioskCode || "······"}</p>
          </div>
          <div className="flex gap-2">
            <BrutalButton data-testid="kiosk-copy-code" variant="green" size="sm" className="flex-1" disabled={!kioskCode} onClick={() => { navigator.clipboard?.writeText(kioskCode || ""); toast.success("Code gekopieerd"); }}><Icons.Copy className="w-4 h-4" strokeWidth={2.5} />Kopieer code</BrutalButton>
            <BrutalButton data-testid="kiosk-regen-code" size="sm" className="flex-1" disabled={regenBusy} onClick={regenerateKiosk}><Icons.RefreshCw className={`w-4 h-4 ${regenBusy ? "animate-spin" : ""}`} strokeWidth={2.5} />Vernieuw code</BrutalButton>
          </div>
          <p className="text-xs text-brand-ink/40">Deel de code alleen met wie de kiosk installeert. Bij vernieuwen werkt de oude code niet meer.</p>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">Uitnodigen & Aanbevelen</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="brutal-sm rounded-xl p-3 bg-brand-cream">
              <p className="font-head font-bold flex items-center gap-2"><Icons.Home className="w-4 h-4" strokeWidth={2.5} />Iemand toevoegen aan {activeOrgObj?.name || "je organisatie"}</p>
              <p className="text-xs text-brand-ink/60 mt-0.5 mb-2">De nieuwe bewoner meldt zich aan; een beheerder keurt goed en er gaat een welkomstmail uit.</p>
              {effOrg && (
                <div className="flex items-center gap-2 bg-white brutal-sm rounded-lg px-3 py-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wide text-brand-ink/50 font-bold">Organisatie-ID</p>
                    <p className="font-head font-black tracking-widest truncate" data-testid="invite-org-id">{effOrg}</p>
                  </div>
                  <button data-testid="invite-copy-orgid" onClick={() => { navigator.clipboard?.writeText(effOrg); toast.success("Organisatie-ID gekopieerd"); }} className="brutal-sm rounded-lg p-2 bg-brand-yellow"><Icons.Copy className="w-4 h-4" strokeWidth={2.5} /></button>
                </div>
              )}
              {effOrg && (
                <div className="flex justify-center mb-2">
                  <img src={joinQr} alt="QR aanmelden" width="140" height="140" className="brutal-sm rounded-xl bg-white p-1" data-testid="invite-join-qr" />
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <BrutalButton data-testid="invite-copy-join" variant="green" size="sm" className="flex-1" onClick={() => { navigator.clipboard?.writeText(joinOrgLink); toast.success("Aanmeldlink gekopieerd"); }}><Icons.Copy className="w-4 h-4" strokeWidth={2.5} />Kopieer link</BrutalButton>
                <BrutalButton data-testid="invite-wa-join" size="sm" className="flex-1" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Hey! Wij regelen ${activeOrgObj?.name || "ons huishouden"} met *Slackr* — taken, boodschappen, klusjes én afspraken sámen, zónder eindeloos te appen.\n\nSluit jij je ook aan? Meld je hier aan (organisatie is al gekoppeld):\n${joinOrgLink}\n\nOf voer bij 'Aansluiten' dit organisatie-ID in: ${effOrg}`)}`, "_blank")}><Icons.MessageCircle className="w-4 h-4" strokeWidth={2.5} />WhatsApp</BrutalButton>
                <BrutalButton data-testid="invite-email-join" variant="pink" size="sm" className="flex-1" onClick={() => window.open(`mailto:?subject=${encodeURIComponent(`Uitnodiging voor ${activeOrgObj?.name || "Slackr"}`)}&body=${encodeURIComponent(`Hoi!\n\nSluit je aan bij ${activeOrgObj?.name || "onze organisatie"} op Slackr. Meld je hier aan (organisatie is al gekoppeld):\n${joinOrgLink}\n\nOf voer bij 'Aansluiten' dit organisatie-ID in: ${effOrg}\n\nTot snel!`)}`)}><Icons.Mail className="w-4 h-4" strokeWidth={2.5} />E-mail</BrutalButton>
              </div>
            </div>
            <div className="brutal-sm rounded-xl p-3 bg-brand-cream">
              <p className="font-head font-bold flex items-center gap-2"><Icons.Share2 className="w-4 h-4" strokeWidth={2.5} />Slackr aanbevelen</p>
              <p className="text-xs text-brand-ink/60 mt-0.5 mb-2">Deel de app met vrienden of andere huizen.</p>
              {refCount > 0 && (() => {
                const tiers = [
                  { min: 1, label: "Starter", icon: "Sprout", cls: "bg-brand-green" },
                  { min: 3, label: "Aanjager", icon: "Flame", cls: "bg-brand-yellow" },
                  { min: 5, label: "Ambassadeur", icon: "Star", cls: "bg-brand-violet text-white" },
                  { min: 10, label: "Superconnector", icon: "Crown", cls: "bg-brand-ink text-white" },
                ];
                const cur = [...tiers].reverse().find((t) => refCount >= t.min) || tiers[0];
                const next = tiers.find((t) => t.min > refCount);
                const TierIcon = Icons[cur.icon];
                const remaining = next ? next.min - refCount : 0;
                const prevMin = cur.min;
                const pct = next ? Math.min(100, Math.round(((refCount - prevMin) / (next.min - prevMin)) * 100)) : 100;
                return (
                  <div className={`brutal-sm rounded-xl p-3 mb-2 ${cur.cls}`} data-testid="referral-counter">
                    <div className="flex items-center gap-2">
                      <TierIcon className="w-6 h-6 shrink-0" strokeWidth={2.5} />
                      <div className="flex-1 min-w-0">
                        <p className="font-head font-black leading-tight" data-testid="referral-tier">{cur.label}</p>
                        <p className="text-xs opacity-90"><span className="font-black">{refCount}</span> {refCount === 1 ? "aanmelding" : "aanmeldingen"} dankzij jou 🎉</p>
                      </div>
                    </div>
                    {next && (
                      <div className="mt-2">
                        <div className="h-2 rounded-full bg-black/15 overflow-hidden">
                          <div className="h-full bg-black/50 rounded-full transition-all" style={{ width: `${pct}%` }} data-testid="referral-progress" />
                        </div>
                        <p className="text-[11px] mt-1 opacity-90">Nog <b>{remaining}</b> {remaining === 1 ? "aanmelding" : "aanmeldingen"} tot <b>{next.label}</b></p>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="relative brutal-sm rounded-xl overflow-hidden bg-white mb-2" data-testid="promo-banner-box">
                <img src={banners[bannerIdx]} alt={`Slackr promo-banner ${bannerIdx + 1}`} decoding="async" className="w-full block transition-opacity" data-testid="promo-banner-img" />
                <button type="button" data-testid="promo-banner-prev" aria-label="Vorige banner" onClick={() => setBannerIdx((i) => (i - 1 + banners.length) % banners.length)} className="absolute left-2 top-1/2 -translate-y-1/2 brutal-sm brutal-press rounded-full p-1.5 bg-white"><Icons.ChevronLeft className="w-5 h-5" strokeWidth={2.5} /></button>
                <button type="button" data-testid="promo-banner-next" aria-label="Volgende banner" onClick={() => setBannerIdx((i) => (i + 1) % banners.length)} className="absolute right-2 top-1/2 -translate-y-1/2 brutal-sm brutal-press rounded-full p-1.5 bg-white"><Icons.ChevronRight className="w-5 h-5" strokeWidth={2.5} /></button>
              </div>
              <div className="flex justify-center gap-1.5 mb-2" data-testid="promo-banner-dots">
                {banners.map((_, i) => (
                  <button key={i} type="button" data-testid={`promo-banner-dot-${i}`} aria-label={`Banner ${i + 1}`} onClick={() => setBannerIdx(i)} className={`h-2.5 rounded-full transition-all ${i === bannerIdx ? "w-6 bg-brand-ink" : "w-2.5 bg-brand-ink/25"}`} />
                ))}
              </div>
              <p className="text-xs text-brand-ink/60 mb-2 flex items-start gap-1.5"><Icons.Instagram className="w-4 h-4 mt-0.5 shrink-0 text-brand-pink" strokeWidth={2.5} /><span>Kies een banner en deel 'm op je <b>Instagram</b>- of <b>Facebook</b>-verhaal. Tag je huisgenoten!</span></p>
              <div className="flex gap-2 flex-wrap mb-2">
                <BrutalButton data-testid="invite-share-banner" variant="pink" size="sm" className="flex-1" onClick={shareBanner}><Icons.Share className="w-4 h-4" strokeWidth={2.5} />Deel banner</BrutalButton>
                <BrutalButton data-testid="invite-download-banner" size="sm" className="flex-1" onClick={downloadBanner}><Download className="w-4 h-4" strokeWidth={2.5} />Download</BrutalButton>
                <BrutalButton data-testid="invite-copy-caption" variant="green" size="sm" className="flex-1" onClick={copyCaption}><Icons.ClipboardCopy className="w-4 h-4" strokeWidth={2.5} />Kopieer bijschrift</BrutalButton>
              </div>

              <div className="flex gap-2">
                <BrutalButton data-testid="invite-copy-app" variant="green" size="sm" className="flex-1" onClick={() => { navigator.clipboard?.writeText(appLink); toast.success("Link gekopieerd"); }}><Icons.Copy className="w-4 h-4" strokeWidth={2.5} />Kopieer link</BrutalButton>
                <BrutalButton data-testid="invite-fb-app" size="sm" className="flex-1" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(appLink)}`, "_blank")}><Icons.Facebook className="w-4 h-4" strokeWidth={2.5} />Facebook</BrutalButton>
                <BrutalButton data-testid="invite-wa-app" size="sm" className="flex-1" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Hey! Wij maken gebruik van *Slackr* — een slim platform om taken, boodschappen, recepten, klusjes en vieringen in huis sámen te regelen. Geen chaos meer, alles overzichtelijk op één plek. Echt een aanrader!\n\nBenieuwd? Check het hier:\n${appLink}`)}`, "_blank")}><Icons.MessageCircle className="w-4 h-4" strokeWidth={2.5} />WhatsApp</BrutalButton>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">Pincode instellen</DialogTitle></DialogHeader>
          <input data-testid="pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric"
            placeholder="4-6 cijfers" className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none text-center text-2xl tracking-widest font-head" />
          <BrutalButton data-testid="save-pin-btn" onClick={savePin} className="w-full">Opslaan</BrutalButton>
        </DialogContent>
      </Dialog>

      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{t("Mijn profiel")}</DialogTitle></DialogHeader>
          <div><label className="text-sm font-bold">{t("Foto")}</label>
            <div className="mt-1"><PhotoInput square initials={initials(profile.name)} value={profile.avatar_url} onChange={(u) => setProfile({ ...profile, avatar_url: u })} aiPrompt="vriendelijke avatar illustratie portret" testid="profile-avatar" /></div>
          </div>
          <div><label className="text-sm font-bold">{t("Naam")}</label>
            <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none mt-1" /></div>
          <div><label className="text-sm font-bold">{t("Telefoonnummer")}</label>
            <p className="text-xs text-brand-ink/50 mb-1">{t("Voor SMS-meldingen. Internationaal formaat, bv. +31 6 12345678")}</p>
            <input data-testid="profile-phone" value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && isValidPhone(v)) setProfile((p) => ({ ...p, phone: normalizePhone(v) })); }}
              inputMode="tel" placeholder="+31 6 12345678"
              className={`w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none ${profile.phone && !isValidPhone(profile.phone) ? "ring-2 ring-red-500" : ""}`} />
            {profile.phone && !isValidPhone(profile.phone)
              ? <p data-testid="profile-phone-error" className="text-xs font-bold text-red-600 mt-1">{t("Vul een geldig telefoonnummer in (bv. +31 6 12345678).")}</p>
              : profile.phone ? <p className="text-xs font-bold text-green-600 mt-1">{t("Ziet er goed uit")} ✓ <span className="text-brand-ink/50 font-medium">({normalizePhone(profile.phone)})</span></p> : null}
          </div>
          <div><label className="text-sm font-bold">{t("Geboortedatum")}</label>
            <DatePicker testid="profile-birthdate" value={profile.birthdate} onChange={(v) => setProfile({ ...profile, birthdate: v })} fromYear={1920} toYear={new Date().getFullYear()} placeholder={t("Kies je geboortedatum")} className="mt-1" />
            <p className="text-xs text-brand-ink/50 mt-1">{t("Je verjaardag verschijnt automatisch bij Vieringen 🎉")}</p></div>

          <div><label className="text-sm font-bold flex items-center gap-1.5"><Icons.Languages className="w-4 h-4" strokeWidth={2.5} />{t("Taal")}</label>
            <div className="grid grid-cols-2 gap-2 mt-1" data-testid="profile-language">
              {[["nl", "🇳🇱 Nederlands"], ["en", "🇬🇧 English"], ["tr", "🇹🇷 Türkçe"], ["ar", "🇲🇦 العربية"]].map(([code, label]) => (
                <button key={code} type="button" data-testid={`lang-${code}`} onClick={() => setProfile({ ...profile, language: code })}
                  className={`brutal-sm brutal-press rounded-xl px-3 py-2.5 text-sm font-bold ${profile.language === code ? "bg-brand-violet text-white" : "bg-brand-cream"}`}>{label}</button>
              ))}
            </div>
            <p className="text-xs text-brand-ink/50 mt-1">{t("De app en berichten van huisgenoten verschijnen in jouw taal.")}</p></div>

          <div className="brutal-sm rounded-xl bg-brand-cream p-3">
            <label className="text-sm font-bold flex items-center gap-1.5"><Icons.PhoneCall className="w-4 h-4" strokeWidth={2.5} />{t("Noodnummer")}</label>
            <p className="text-xs text-brand-ink/50 mb-2">{t("Kies een huisgenoot of vul een extern nummer in.")}</p>
            <select data-testid="profile-ec-member" value={profile.emergency_contact?.member_id || ""}
              onChange={(e) => setEc({ member_id: e.target.value, ...(e.target.value ? { name: "", phone: "" } : {}) })}
              className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none bg-white">
              <option value="">{t("— Extern nummer —")}</option>
              {members.filter((m) => m.user_id !== user.user_id).map((m) => <option key={m.user_id} value={m.user_id}>{m.name}{m.phone ? ` (${m.phone})` : ""}</option>)}
            </select>
            {!profile.emergency_contact?.member_id && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <input data-testid="profile-ec-name" value={profile.emergency_contact?.name || ""} onChange={(e) => setEc({ name: e.target.value })} placeholder={t("Naam")} className="brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
                <input data-testid="profile-ec-phone" value={profile.emergency_contact?.phone || ""} onChange={(e) => setEc({ phone: e.target.value })} inputMode="tel" placeholder={t("Telefoon")} className="brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-bold flex items-center gap-1.5"><Icons.PawPrint className="w-4 h-4" strokeWidth={2.5} />{t("Huisdieren")}</label>
              <button type="button" data-testid="add-pet-btn" onClick={addPet} className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-yellow flex items-center gap-1"><Icons.Plus className="w-3.5 h-3.5" strokeWidth={2.5} />{t("Huisdier")}</button>
            </div>
            <div className="space-y-3 mt-2">
              {profile.pets.length === 0 && <p className="text-xs text-brand-ink/40">{t("Nog geen huisdier toegevoegd.")}</p>}
              {profile.pets.map((pet) => (
                <div key={pet.id} data-testid={`pet-${pet.id}`} className="brutal-sm rounded-xl bg-brand-cream p-3">
                  <div className="flex items-start gap-3">
                    <PhotoInput square initials={initials(pet.name || "?")} value={pet.photo_url} onChange={(u) => updatePet(pet.id, { photo_url: u })} aiPrompt="schattige huisdier illustratie portret" testid={`pet-photo-${pet.id}`} />
                    <div className="flex-1 space-y-2">
                      <input data-testid={`pet-name-${pet.id}`} value={pet.name} onChange={(e) => updatePet(pet.id, { name: e.target.value })} placeholder={t("Naam")} className="w-full brutal-sm rounded-lg px-3 py-2 font-medium outline-none bg-white" />
                      <input data-testid={`pet-species-${pet.id}`} value={pet.species || ""} onChange={(e) => updatePet(pet.id, { species: e.target.value })} placeholder={t("Soort (hond, kat…)")} className="w-full brutal-sm rounded-lg px-3 py-2 font-medium outline-none bg-white" />
                    </div>
                    <button type="button" data-testid={`pet-remove-${pet.id}`} onClick={() => removePet(pet.id)} className="brutal-sm brutal-press rounded-full p-1.5 bg-white shrink-0"><Icons.Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                  </div>
                  <input data-testid={`pet-chip-${pet.id}`} value={pet.chip_id || ""} onChange={(e) => updatePet(pet.id, { chip_id: e.target.value })} placeholder={t("Chipnummer")} className="w-full brutal-sm rounded-lg px-3 py-2 font-medium outline-none bg-white mt-2" />
                  <textarea data-testid={`pet-desc-${pet.id}`} value={pet.description || ""} onChange={(e) => updatePet(pet.id, { description: e.target.value })} rows={2} placeholder={t("Omschrijving (ras, kleur, bijzonderheden…)")} className="w-full brutal-sm rounded-lg px-3 py-2 font-medium outline-none bg-white mt-2" />
                </div>
              ))}
            </div>
          </div>

          <BrutalButton data-testid="save-profile-btn" onClick={saveProfile} className="w-full">{t("Opslaan")}</BrutalButton>

          <div className="mt-4 pt-4 border-t-2 border-dashed border-brand-ink/15">
            <p className="text-xs font-bold text-brand-ink/50 uppercase tracking-wide mb-1.5">Gevarenzone</p>
            <div className="brutal-sm rounded-xl bg-brand-pink/10 p-3 flex items-start gap-2 mb-2">
              <Icons.AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-brand-pink" strokeWidth={2.5} />
              <p className="text-xs font-medium">Bij opheffen wordt je account definitief verwijderd uit <b>álle</b> huishoudens. Ben je de laatste bewoner, dan wordt dat huishouden met álle data ook verwijderd. Dit kan niet ongedaan gemaakt worden.</p>
            </div>
            <button data-testid="delete-account-btn" onClick={deleteAccount}
              className="w-full brutal-sm brutal-press rounded-xl py-2.5 bg-brand-pink text-white font-head font-bold flex items-center justify-center gap-2"><Icons.Trash2 className="w-4 h-4" strokeWidth={2.5} />Account opheffen</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={delAccountOpen} onOpenChange={(o) => !o && !delAccountBusy && setDelAccountOpen(false)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[88vh] overflow-auto" data-testid="delete-account-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl flex items-center gap-2"><Icons.Trash2 className="w-5 h-5 text-brand-pink" strokeWidth={2.5} />Account opheffen</DialogTitle></DialogHeader>
          <div className="brutal-sm rounded-xl bg-brand-pink/10 p-3 flex items-start gap-2">
            <Icons.AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-brand-pink" strokeWidth={2.5} />
            <p className="text-xs font-medium">Definitief. Je wordt uit <b>álle</b> huishoudens verwijderd; ben je de laatste bewoner, dan wordt dat huishouden met álle data ook gewist.</p>
          </div>
          <p className="text-sm font-bold mt-1">Waarom vertrek je? <span className="font-normal text-brand-ink/50">(optioneel, helpt ons verbeteren)</span></p>
          <div className="space-y-1.5">
            {[
              ["niet_gebruikt", "Ik gebruik het niet meer"],
              ["te_ingewikkeld", "Te ingewikkeld in gebruik"],
              ["verhuisd", "Verhuisd / huishouden opgeheven"],
              ["mist_functies", "Ik mis functies"],
              ["privacy", "Privacy / gegevens"],
              ["anders", "Anders"],
            ].map(([val, label]) => (
              <button key={val} data-testid={`churn-${val}`} onClick={() => setChurnReason(val)}
                className={`w-full brutal-sm brutal-press rounded-xl px-3 py-2 text-left text-sm font-bold flex items-center gap-2 ${churnReason === val ? "bg-brand-violet text-white" : "bg-brand-cream"}`}>
                <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${churnReason === val ? "bg-white border-white" : "border-brand-ink/30"}`} />{label}
              </button>
            ))}
          </div>
          <textarea data-testid="churn-text" value={churnText} onChange={(e) => setChurnText(e.target.value)} rows={2}
            placeholder="Iets toevoegen? (optioneel)" className="w-full brutal-sm rounded-xl p-2.5 text-sm min-w-0" />
          <div className="flex gap-2 pt-1">
            <button data-testid="churn-cancel" disabled={delAccountBusy} onClick={() => setDelAccountOpen(false)}
              className="flex-1 brutal-sm brutal-press rounded-xl py-2.5 bg-white font-head font-bold disabled:opacity-50">Annuleren</button>
            <button data-testid="churn-confirm" disabled={delAccountBusy} onClick={confirmDeleteAccount}
              className="flex-1 brutal brutal-press rounded-xl py-2.5 bg-brand-pink text-white font-head font-bold disabled:opacity-50">{delAccountBusy ? "Bezig…" : "Definitief opheffen"}</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={installOpen} onOpenChange={setInstallOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-w-md max-h-[85vh] overflow-auto" data-testid="install-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-2xl flex items-center gap-2"><Download className="w-6 h-6" strokeWidth={2.5} />App installeren</DialogTitle></DialogHeader>
          <p className="text-sm text-brand-ink/60 -mt-1">Zet Slackr op je beginscherm — zo open je 'm met één tik én werken pushmeldingen.</p>

          <div className="flex gap-2 mt-1">
            <button data-testid="install-mode-app" onClick={() => setInstallMode("app")}
              className={`flex-1 brutal-sm brutal-press rounded-xl py-2.5 font-head font-bold text-sm ${installMode === "app" ? "bg-brand-ink text-white" : "bg-white"}`}>Deze telefoon</button>
            <button data-testid="install-mode-kiosk" onClick={() => setInstallMode("kiosk")}
              className={`flex-1 brutal-sm brutal-press rounded-xl py-2.5 font-head font-bold text-sm ${installMode === "kiosk" ? "bg-brand-ink text-white" : "bg-white"}`}>Kiosk (tablet)</button>
          </div>

          {installMode === "app" && (<>
          <div className="brutal-sm rounded-xl bg-white p-3 mt-1 text-center" data-testid="app-join-qr-box">
            <p className="text-sm font-bold mb-2 flex items-center justify-center gap-1.5"><Icons.QrCode className="w-4 h-4" strokeWidth={2.5} />Scan om aan te melden</p>
            <div className="flex justify-center">
              <img src={joinQr} alt="QR-code aanmelden" width="170" height="170" className="brutal-sm rounded-xl bg-white p-1" data-testid="app-join-qr" />
            </div>
            <p className="text-xs text-brand-ink/60 mt-2">Handig om onderling te delen — deze code koppelt de nieuwe gebruiker meteen aan jullie organisatie.</p>
          </div>
          {installable && (
            <BrutalButton data-testid="do-install-btn" onClick={doInstall} className="w-full mt-1"><Download className="w-5 h-5" strokeWidth={2.5} />Nu installeren</BrutalButton>
          )}

          <div className="brutal-sm rounded-xl bg-brand-cream p-3 mt-2">
            <p className="font-head font-black flex items-center gap-2"><Icons.Smartphone className="w-5 h-5" strokeWidth={2.5} />Android (Chrome)</p>
            <ol className="text-sm text-brand-ink/80 list-decimal ml-5 mt-1.5 space-y-1">
              <li>Tik op <b>Installeren</b> hieronder, of open het menu <b>⋮</b> → <b>App installeren</b>.</li>
              <li>Bevestig met <b>Installeren</b> en open Slackr via het nieuwe icoon.</li>
            </ol>
            <BrutalButton data-testid="android-install-btn" onClick={doInstall} size="sm" className="w-full mt-2.5"><Download className="w-4 h-4" strokeWidth={2.5} />Installeren</BrutalButton>
            <p className="text-xs mt-2 flex items-start gap-1.5"><Icons.Bell className="w-4 h-4 mt-0.5 shrink-0 text-brand-green" strokeWidth={2.5} /><span><b>Meldingen:</b> kies <b>Toestaan</b> als je browser erom vraagt. Staat het uit? Ga naar Instellingen → Apps → Slackr → Meldingen.</span></p>
          </div>

          <div className={`brutal-sm rounded-xl bg-brand-cream p-3 mt-2 transition-shadow ${iosPulse ? "ring-4 ring-brand-pink animate-pulse" : ""}`}>
            <p className="font-head font-black flex items-center gap-2"><Icons.Apple className="w-5 h-5" strokeWidth={2.5} />iPhone / iPad (Safari)</p>
            <ol className="text-sm text-brand-ink/80 list-decimal ml-5 mt-1.5 space-y-1">
              <li>Tik op het <b>Deel</b>-icoon <Icons.Share className="w-4 h-4 inline" strokeWidth={2.5} /> onderin Safari.</li>
              <li>Kies <b>Zet op beginscherm</b>.</li>
              <li>Tik <b>Voeg toe</b> en open Slackr via het icoon.</li>
            </ol>
            <BrutalButton data-testid="ios-install-btn" variant="pink" onClick={showIosSteps} size="sm" className="w-full mt-2.5"><Icons.Share className="w-4 h-4" strokeWidth={2.5} />Toon mij hoe</BrutalButton>
            <p className="text-xs mt-2 flex items-start gap-1.5"><Icons.AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-brand-pink" strokeWidth={2.5} /><span><b>Belangrijk:</b> op iPhone/iPad werken pushmeldingen <b>alléén</b> als de app op je beginscherm staat (iOS 16.4 of nieuwer). Open daarna de app en zet meldingen aan bij <b>Meer</b>.</span></p>
          </div>
          </>)}

          {installMode === "kiosk" && (
          <div className="mt-1">
            <p className="text-sm text-brand-ink/70">Zet Slackr als infobord op een tablet (11") aan de muur. Scan de code met de tablet:</p>
            <div className="flex justify-center my-3">
              <img src={kioskQr} alt="QR-code kiosk" width="180" height="180" className="brutal-sm rounded-xl bg-white p-1" data-testid="kiosk-install-qr" />
            </div>
            <p className="text-xs text-brand-ink/60 text-center -mt-1 mb-2">Handig om onderling te delen — deze code koppelt de tablet meteen aan jullie organisatie.</p>
            {isAdmin(user) ? (
              <div className="brutal-sm rounded-xl bg-brand-yellow p-3" data-testid="kiosk-code-box">
                <p className="font-head font-black flex items-center gap-2"><KeyRound className="w-5 h-5" strokeWidth={2.5} />Verificatiecode</p>
                <p className="font-head font-black text-3xl tracking-[0.3em] text-center my-2">{kioskCode || "…"}</p>
                <p className="text-xs text-brand-ink/70">De tablet vraagt deze code <b>één keer</b> na het openen. Geef 'm door aan wie de kiosk installeert. Bewaar 'm veilig.</p>
              </div>
            ) : (
              <div className="brutal-sm rounded-xl bg-brand-cream p-3">
                <p className="text-sm"><b>Let op:</b> voor de kiosk is een verificatiecode nodig. Vraag je beheerder om deze code.</p>
              </div>
            )}
            <div className="brutal-sm rounded-xl bg-brand-cream p-3 mt-2">
              <p className="text-xs text-brand-ink/70">Op de tablet: open de link, <b>zet op beginscherm</b> (zoals de app), open de kiosk en voer de code in.</p>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

      <NotifPrefsDialog open={notifOpen} onClose={() => setNotifOpen(false)} pushOn={pushOn} onTogglePush={togglePush} pushSupported={pushSupported()} pushBusy={busy} />
    </div>
  );
}
